import { useState, useEffect } from "react"
import { PlusIcon, TrashIcon, ChevronDownIcon } from "@heroicons/react/24/outline"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { api } from "aldoo-ra/API"

const crudAPI = api("crud")

const emptyForm = { groupId: "", name: "", allowMultiple: false, description: "" }

export default function ManageGroupsDialog({ isOpen, onClose }) {
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [expandedId, setExpandedId] = useState(null)
  const [showForm, setShowForm] = useState(false)

  const fetchGroups = async () => {
    setLoading(true)
    try {
      const res = await crudAPI({
        get: {
          model: "ProductSubscriptionGroup",
          match: { deleted: { $ne: true } },
          sort: { name: 1 }
        }
      })
      setGroups(Array.isArray(res) ? res : (res?.data ?? []))
    } catch (e) {
      console.error("Failed to fetch groups:", e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      fetchGroups()
      setForm(emptyForm)
      setShowForm(false)
    }
  }, [isOpen])

  const handleAdd = async () => {
    if (!form.groupId.trim()) {
      showNotification({ message: "Group ID is required", className: "bg-admin_error text-admin_text_inv" })
      return
    }
    if (!form.name.trim()) {
      showNotification({ message: "Name is required", className: "bg-admin_error text-admin_text_inv" })
      return
    }

    setSaving(true)
    try {
      await crudAPI({
        create: {
          model: "ProductSubscriptionGroup",
          data: {
            groupId: form.groupId.trim().toLowerCase().replace(/\s+/g, "-"),
            name: form.name.trim(),
            allowMultiple: form.allowMultiple,
            description: form.description.trim(),
          }
        }
      })
      showNotification({ message: "Group created", className: "bg-admin_primary text-admin_text_inv" })
      setForm(emptyForm)
      setShowForm(false)
      fetchGroups()
    } catch (e) {
      showNotification({ message: "Failed to create group", className: "bg-admin_error text-admin_text_inv" })
    } finally {
      setSaving(false)
    }
  }

  const handleToggleAllowMultiple = async (group, value) => {
    setGroups((prev) => prev.map((g) => g._id === group._id ? { ...g, allowMultiple: value } : g))
    try {
      await crudAPI({
        update: {
          model: "ProductSubscriptionGroup",
          data: { _id: group._id, allowMultiple: value }
        }
      })
    } catch (e) {
      setGroups((prev) => prev.map((g) => g._id === group._id ? { ...g, allowMultiple: group.allowMultiple } : g))
      showNotification({ message: "Failed to update group", className: "bg-admin_error text-admin_text_inv" })
    }
  }

  const handleDelete = (group) => {
    showPromptDialog({
      title: "Delete Group",
      message: `Delete "${group.name}"? Products assigned to this group will lose their group assignment.`,
      confirmText: "Delete",
      onConfirm: async () => {
        try {
          await crudAPI({
            delete: {
              model: "ProductSubscriptionGroup",
              mode: "soft",
              match: { _id: group._id }
            }
          })
          showNotification({ message: "Group deleted", className: "bg-admin_primary text-admin_text_inv" })
          fetchGroups()
        } catch (e) {
          showNotification({ message: "Failed to delete group", className: "bg-admin_error text-admin_text_inv" })
        }
      }
    })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} variant="admin" className="w-full sm:w-[560px]">
      <div className="flex flex-col gap-4 p-6">

        {/* Title + Add button */}
        <div className="flex items-center justify-between">
          <Typography variant="admin_h4">Subscription Groups</Typography>
          <Button variant="admin_primary" onClick={() => { setShowForm(true); setForm(emptyForm) }} disabled={showForm}>
            <div className="flex items-center gap-2">
              <PlusIcon className="w-4 h-4" />
              Add Group
            </div>
          </Button>
        </div>

        {/* Collapsible create form */}
        {showForm && (
          <div className="rounded-xl border border-admin_bordered bg-admin_neutral/30 p-4 flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-xs text-admin_text_secondary font-medium">Group ID *</label>
                <input
                  value={form.groupId}
                  onChange={(e) => setForm((f) => ({ ...f, groupId: e.target.value }))}
                  placeholder="e.g. product-plans"
                  className="rounded-lg border border-admin_bordered bg-admin_paper px-3 py-1.5 text-sm text-admin_text focus:outline-none focus:ring-1 focus:ring-admin_primary"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-admin_text_secondary font-medium">Name *</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Product Plans"
                  className="rounded-lg border border-admin_bordered bg-admin_paper px-3 py-1.5 text-sm text-admin_text focus:outline-none focus:ring-1 focus:ring-admin_primary"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-admin_text_secondary font-medium">Description</label>
              <input
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Optional description"
                className="rounded-lg border border-admin_bordered bg-admin_paper px-3 py-1.5 text-sm text-admin_text focus:outline-none focus:ring-1 focus:ring-admin_primary"
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.allowMultiple}
                onChange={(e) => setForm((f) => ({ ...f, allowMultiple: e.target.checked }))}
                className="rounded border-admin_bordered text-admin_primary"
              />
              <span className="text-sm text-admin_text">Allow multiple active subscriptions in this group</span>
            </label>

            <div className="flex items-center justify-end gap-2">
              <Button variant="admin_secondary" onClick={() => { setShowForm(false); setForm(emptyForm) }}>
                Hide
              </Button>
              <Button variant="admin_primary" onClick={handleAdd} disabled={saving}>
                Create
              </Button>
            </div>
          </div>
        )}

        {/* Groups list */}
        <div className="flex flex-col gap-2">
          {loading ? (
            <div className="h-16 rounded-xl bg-admin_neutral animate-pulse" />
          ) : groups.length === 0 ? (
            <Typography variant="admin_body" className="text-admin_text_secondary text-center py-4">
              No groups yet
            </Typography>
          ) : (
            groups.map((g) => {
              const expanded = expandedId === g._id
              return (
                <div
                  key={g._id}
                  className="rounded-xl border border-admin_bordered bg-admin_paper overflow-hidden"
                >
                  <button
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-admin_neutral/30 transition-colors"
                    onClick={() => setExpandedId(expanded ? null : g._id)}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-semibold text-sm text-admin_text truncate">{g.name}</span>
                      <span className="text-xs text-admin_text_secondary font-mono shrink-0">{g.groupId}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-xs font-medium ${g.allowMultiple ? "text-admin_success" : "text-admin_warning"}`}>
                        {g.allowMultiple ? "Multiple" : "Single"}
                      </span>
                      <ChevronDownIcon className={`w-4 h-4 text-admin_text_secondary transition-transform ${expanded ? "rotate-180" : ""}`} />
                    </div>
                  </button>

                  {expanded && (
                    <div className="px-4 pb-3 pt-1 border-t border-admin_bordered flex items-center justify-between gap-4">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={g.allowMultiple}
                          onChange={(e) => handleToggleAllowMultiple(g, e.target.checked)}
                          className="rounded border-admin_bordered text-admin_primary"
                        />
                        <span className="text-sm text-admin_text">Allow multiple active subscriptions</span>
                      </label>
                      <Button
                        variant="admin_icon"
                        className="text-admin_text_secondary hover:text-admin_error shrink-0"
                        onClick={() => handleDelete(g)}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>
    </Modal>
  )
}
